import type { InputFrame } from "../input";
import { CHUNK, closeCalls, closeShaves, courseHit, hornAhead, onGravel, type HillHitKind } from "../hill-course";
import { placeRoad } from "../hill-ramps";
import { endScore, hillScore, scoreStep } from "../hill-score";
import { halfZ, hillDrop } from "../level";
import type { Skater } from "../skater";
import { bail } from "./bail";
import type { RideId } from "./rides";
import type { HillEnd, HillHit, HillState, SkaterEvent } from "./state";
import {
  AUTO_PUSH,
  CARVE_EASE,
  CARVE_SCRUB,
  CARVE_TURN,
  HILL_CLOSE_BONUS,
  HILL_FINISH,
  HIT_BOUNCE,
  HILL_MAX_SPEED,
  HILL_PUSH_SPEED,
  HILL_RECENTRE,
  HILL_RESET,
  ROLL_FRICTION,
  SLIDE_CARVE,
  SLIDE_SCRUB,
  SLIDE_SPEED,
  SLIP_CALM,
  SLIP_RATE,
  WOBBLE_CALM,
  WOBBLE_CARVE_CALM,
  WOBBLE_RATE,
  WOBBLE_SPEED,
} from "./tuning";

// Bomb the Hill (#193): on a hill map the ground is ridden differently.
// The hill keeps pulling you faster (HILL_G, in `ground.ts`), there's no
// pushing past the first few metres and no brake: down on the stick does
// nothing. The only brake is the carve: hold left or right and the rider
// leans into a turn that scrubs speed, more the harder and longer you hold
// it, so swinging side to side across the road holds a safe speed. Too
// fast for too long and speed wobbles build to a face plant; carving hard
// at speed slides the wheels out, a big brake that can throw you. Either
// ends the run, like any bail, and you're back at the top.

/** The rides you can pick at the top of the hill (#195), in the order the stick steps through them. */
export const HILL_RIDES: readonly RideId[] = ["board", "bmx", "scooter", "skates"];

/** A fresh run starting at `z` on `ride`, waiting at the top for a pick (#195), on the road laid out from `seed`. */
export function newHill(z: number, ride: RideId, seed = 0): HillState {
  return { startZ: z, distance: 0, bonus: 0, closeCalls: 0, lastZ: z, carve: 0, wobble: 0, sliding: false, slip: 0, end: null, endT: 0, hit: null, gravel: false, picking: true, pickDir: 0, ride, seed, honked: -Infinity, speedPts: 0, wobblePts: 0, trickPts: 0, slowT: 0 };
}

/** A push on the stick past this, more sideways than up or down, steps the ride (#207). */
const PICK_PUSH = 0.5;
/** ...and it has to come back inside this before the next push counts, so a wobbling stick steps once. */
const PICK_REARM = 0.25;

/** Start on a pad drops in while you're picking a ride (#207), so it goes to the hill, not the pause menu. */
export function startDropsIn(h: HillState | null, input: InputFrame): boolean {
  return h !== null && h.picking && input.source === "gamepad";
}

/** Steps `sk`'s ride one along `HILL_RIDES` (`dir` -1 or 1), wrapping round. */
function nextRide(sk: Skater, dir: number): void {
  const at = Math.max(0, HILL_RIDES.indexOf(sk.ride));
  sk.ride = HILL_RIDES[(at + dir + HILL_RIDES.length) % HILL_RIDES.length]!;
  sk.pack.item = sk.ride;
}

/** The pick card's arrows (#203): steps the ride while you're picking. False (and nothing changes) otherwise. */
export function hillStepRide(sk: Skater, dir: -1 | 1): boolean {
  const h = sk.hill;
  if (!h?.picking) return false;
  nextRide(sk, dir);
  h.ride = sk.ride;
  return true;
}

/** A tap on the pick card's ride (#203): drops in on it, like ✕. False if you're not picking. */
export function hillDropIn(sk: Skater): boolean {
  const h = sk.hill;
  if (!h?.picking) return false;
  h.picking = false;
  return true;
}

/**
 * At the top of the hill (#195): standing still, a clear push left or right
 * steps through the rides, once per push, and ollie (✕ / A, or Start on a
 * pad) drops in. Up on the stick doesn't (#207): a diagonal or drift on a
 * pad dropped you in by accident. Returns true while you're still picking
 * (the hill's ground step does nothing else then).
 */
function pickRide(sk: Skater, h: HillState, input: InputFrame): boolean {
  if (!h.picking) return false;
  sk.speed = 0;
  h.carve = 0;
  const x = input.moveX;
  if (h.pickDir === 0 && Math.abs(x) > PICK_PUSH && Math.abs(x) > Math.abs(input.moveY)) {
    h.pickDir = Math.sign(x);
    nextRide(sk, h.pickDir);
  } else if (h.pickDir !== 0 && (Math.abs(x) < PICK_REARM || Math.sign(x) !== h.pickDir)) h.pickDir = 0;
  h.ride = sk.ride;
  if (input.pressed.ollie || (input.pressed.pause && startDropsIn(h, input))) h.picking = false;
  return h.picking;
}

/**
 * The hill's turn on the ground, in place of steering, pushing and braking:
 * the carve turns you and scrubs speed, and too much speed wobbles. Returns
 * true if it threw you (a bail).
 */
export function stepHillGround(sk: Skater, h: HillState, input: InputFrame, dt: number): boolean {
  if (pickRide(sk, h, input)) return false;
  h.carve += (input.moveX - h.carve) * Math.min(1, CARVE_EASE * dt);
  // On gravel (#194) the wheels can't bite: the carve neither turns nor brakes you.
  const course = sk.level.hill?.road?.course;
  h.gravel = course !== undefined && onGravel(course, sk.x, sk.z);
  const grip = h.gravel ? 0 : 1;
  const carve = Math.abs(h.carve) * grip;
  sk.yaw -= h.carve * grip * CARVE_TURN * dt;

  const sign = sk.speed >= 0 ? 1 : -1;
  let mag = Math.abs(sk.speed);
  if (mag < HILL_PUSH_SPEED && h.end === null) mag = Math.min(HILL_PUSH_SPEED, mag + AUTO_PUSH * dt);
  mag -= CARVE_SCRUB * carve * mag * dt;
  h.sliding = carve > SLIDE_CARVE && mag > SLIDE_SPEED;
  if (h.sliding) {
    h.slip += (mag - SLIDE_SPEED) * SLIP_RATE * dt;
    mag -= SLIDE_SCRUB * dt;
  } else h.slip = Math.max(0, h.slip - SLIP_CALM * dt);
  if (mag > WOBBLE_SPEED) h.wobble += (mag - WOBBLE_SPEED) * WOBBLE_RATE * (1 - WOBBLE_CARVE_CALM * carve) * dt;
  else h.wobble = Math.max(0, h.wobble - WOBBLE_CALM * dt);
  mag = Math.max(0, Math.min(HILL_MAX_SPEED, mag - ROLL_FRICTION * dt));
  sk.speed = mag * sign;

  const thrown: HillEnd | null = h.slip >= 1 ? "slide" : h.wobble >= 1 ? "plant" : null;
  if (!thrown) return false;
  h.wobble = Math.min(1, h.wobble);
  h.slip = Math.min(1, h.slip);
  h.sliding = false;
  endRun(sk, h, thrown, sk.out);
  bail(sk, sk.out);
  return true;
}

/**
 * The run each step, whatever the mode: the distance down, the end of the
 * run (any bail, or the bottom of a fixed hill), and back to the top a
 * moment after it ends.
 */
export function stepHill(sk: Skater, h: HillState, dt: number, events: SkaterEvent[]): void {
  if (h.end === null) {
    h.distance = Math.max(h.distance, sk.z - h.startZ);
    if (sk.mode !== "ground") h.gravel = false;
    const hit = sk.mode === "bail" ? null : hitAhead(sk);
    if (hit) {
      // Into something in the road (#194): the run's over.
      endRun(sk, h, "hit", events, hit);
      bail(sk, events, hit !== "pothole");
      // A pothole pitches you on down the hill; anything standing stops you dead.
      if (hit !== "pothole") {
        sk.vx *= -HIT_BOUNCE;
        sk.vz *= -HIT_BOUNCE;
      }
    } else if (sk.mode === "bail") endRun(sk, h, "crash", events);
    else if (sk.z >= halfZ(sk.level) - HILL_FINISH) endRun(sk, h, "bottom", events);
    else passBy(sk, h, events);
    // The run's speed points (#205), once you've dropped in.
    if (h.end === null && !h.picking) scoreStep(h, sk.horizontalSpeed, sk.z - h.lastZ, dt, sk.combo.active);
    if (sk.mode !== "ground") h.sliding = false;
    h.lastZ = sk.z;
    if (h.end === null) recentre(sk, h, events);
    return;
  }
  h.sliding = false;
  h.gravel = false;
  h.endT += dt;
  if (h.endT < HILL_RESET) return;
  sk.respawn();
  events.push({ type: "respawn" });
}

/**
 * The road is endless (#195): once the rider's `CHUNK` metres past
 * `HILL_RECENTRE` below the top, on the ground, the road and the rider move
 * back up by `CHUNK` together and the course is laid out further on. Only
 * on the ground, where nothing the rider's doing remembers a place.
 */
function recentre(sk: Skater, h: HillState, events: SkaterEvent[]): void {
  const hill = sk.level.hill;
  const road = hill?.road;
  if (!hill || !road || sk.mode !== "ground" || sk.z < road.top + HILL_RECENTRE + CHUNK) return;
  road.advance(sk.clock);
  placeRoad(sk.level);
  const dy = hill.grade * CHUNK;
  sk.z -= CHUNK;
  sk.y += dy;
  h.startZ -= CHUNK;
  h.lastZ -= CHUNK;
  sk.lastSafe.z -= CHUNK;
  if (sk.manualFrom) sk.manualFrom.z -= CHUNK;
  events.push({ type: "hillShift", dz: -CHUNK, dy });
}

/**
 * Into a wall on the hill (#195): the sides of the road, a ramp's side,
 * anything you'd bump or hang on in a park, ends the run, unless you're
 * wallriding or planting off it. Returns true if it did.
 */
export function hillWall(sk: Skater, events: SkaterEvent[]): boolean {
  const h = sk.hill;
  if (!h || h.end !== null) return false;
  endRun(sk, h, "hit", events, "wall");
  sk.vx = sk.vz = 0;
  sk.speed = 0;
  bail(sk, events);
  return true;
}

/**
 * Close calls (#196): anyone on a crosswalk the rider just went by without
 * hitting, close enough to make them jump, adds `HILL_CLOSE_BONUS` metres
 * to the run, and so does a close shave with a side street's car (#197).
 * A side street's car coming for the rider's line honks first, once a side
 * street.
 */
function passBy(sk: Skater, h: HillState, events: SkaterEvent[]): void {
  const course = sk.level.hill?.road?.course;
  if (!course || h.picking) return;
  for (const w of closeCalls(course, sk.x, h.lastZ, sk.z, sk.clock)) {
    h.bonus += HILL_CLOSE_BONUS;
    h.closeCalls++;
    events.push({ type: "hillClose", x: sk.x, z: w.z, bonus: HILL_CLOSE_BONUS });
  }
  for (const c of closeShaves(course, sk.x, h.lastZ, sk.z, sk.clock)) {
    h.bonus += HILL_CLOSE_BONUS;
    h.closeCalls++;
    events.push({ type: "hillShave", x: sk.x, z: c.z, bonus: HILL_CLOSE_BONUS });
  }
  const horn = hornAhead(course, sk.x, sk.z, sk.horizontalSpeed, sk.clock);
  const at = horn ? Math.round(horn.z + course.offset) : null;
  if (at !== null && at > h.honked) {
    h.honked = at;
    events.push({ type: "hillHorn" });
  }
}

/** What the skater has run into on the hill's course (#194), if anything: not while grinding, and only a pothole under their wheels on the ground. */
function hitAhead(sk: Skater): HillHitKind | null {
  const course = sk.level.hill?.road?.course;
  if (!course || sk.mode === "grind") return null;
  const up = sk.y + hillDrop(sk.level, sk.z);
  return courseHit(course, sk.x, sk.z, up, sk.mode === "ground", sk.clock);
}

function endRun(sk: Skater, h: HillState, end: HillEnd, events: SkaterEvent[], hit: HillHit | null = null): void {
  h.distance = Math.max(h.distance, sk.z - h.startZ);
  h.end = end;
  h.endT = 0;
  h.hit = hit;
  // The run's distance is how far down it got plus its close calls (#196); its score (#205) adds speed and tricks.
  endScore(h);
  const score = hillScore(h);
  const distance = score.distance;
  events.push(hit ? { type: "hill", end, distance, hit, score } : { type: "hill", end, distance, score });
}
